import { Localized } from "@fluent/react";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { useCallback, useState } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { DeleteButton } from "../../../../app/components/DeleteButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { deletepProcess } from "../../projectProcess/ProjectProcessApiClient";

export interface DeleteValidationProcessProps {
    processId: AppId;
    onSuccessfullDeleted: (processId: AppId) => void;
    onCancel: () => void;
}

export const DeleteValidationProcess = ({
    processId,
    onSuccessfullDeleted,
    onCancel,
}: DeleteValidationProcessProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const [password, setPassword] = useState("")
    const handlePasswordFieldChange: AppTextFieldHandler = useCallback(e => {
        setPassword(e.target.value)
    }, [])

    const handleConfirmDeleteClick = async () => {
        setSaving(true);
        try {
            if (password && processId) {
                const result = await dispatch(deletepProcess({
                    id: processId,
                    args: { password }
                }))

                if (deletepProcess.fulfilled.match(result)) {
                    dispatch(showSuccess("deleted"));
                    onSuccessfullDeleted(processId);
                } else {
                    let errorMsg = "error";
                    if (result.payload && result.payload.kind === "http") {
                        const problem = result.payload.problem;
                        if (problem) {
                            errorMsg = problem.title;
                        }
                    }
                    setServerError(errorMsg);
                }
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    minWidth: { xs: "auto", sm: 300 },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "grey.200",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                <Localized id="process-delete-confirmation">
                    <span>_delete</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", }}>
                    <Stack spacing={1.5} sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
                            <WarningAmberIcon color="error" sx={{ fontSize: 18 }} />
                            <Typography variant="body2" color="error.main" sx={{ fontSize: "0.85rem", lineHeight: 1.35 }}>
                                <Localized id="process-delete-warning">
                                    <span>_delete warning</span>
                                </Localized>
                            </Typography>
                        </Stack>
                        <TextField
                            sx={{
                                marginTop: 1
                            }}
                            fullWidth
                            autoFocus
                            type="password"
                            value={password}
                            onChange={handlePasswordFieldChange}
                            label={<Localized id="password"><span>Hasło</span></Localized>}
                            helperText={<Localized id="autorization-password-required"><span>Wymagana autoryzacja hasłem</span></Localized>}
                            autoComplete="off"
                        />
                    </Stack>

                </Box>

                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <DeleteButton loading={saving} onClick={handleConfirmDeleteClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
